import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Table, Button, Modal, Form, Spinner, Alert } from 'react-bootstrap';

interface LabourCost {
  id: number;
  employeeEmail: string;
  cost: number;
  effectiveStartDate: string;
  effectiveEndDate?: string | null;
}

const LabourCostsPage: React.FC = () => {
  const [labourCosts, setLabourCosts] = useState<LabourCost[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<LabourCost | null>(null);
  const [formData, setFormData] = useState({
    employeeEmail: '',
    cost: '',
    effectiveStartDate: '',
    effectiveEndDate: '',
  });
  const [salaryInput, setSalaryInput] = useState('');
  const [hoursPerDayInput, setHoursPerDayInput] = useState('8');
  const [helperTouched, setHelperTouched] = useState(false);
  const [isApprentice, setIsApprentice] = useState(false);

  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }),
    []
  );

  const parsedSalary = useMemo(() => {
    const numericValue = parseFloat(salaryInput.replace(/[\s,]/g, ''));
    if (!Number.isFinite(numericValue) || numericValue < 0) {
      return 0;
    }
    return numericValue;
  }, [salaryInput]);

  const parsedHoursPerDay = useMemo(() => {
    const numericValue = parseFloat(hoursPerDayInput);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return 0;
    }
    return numericValue;
  }, [hoursPerDayInput]);

  const helperCalculations = useMemo(() => {
    const workingHoursPerYear = parsedHoursPerDay > 0 ? parsedHoursPerDay * 5 * 52 : 0;

    // Employer NI is 15% of earnings above the £5,000 threshold (unless apprentice).
    const employerNi = isApprentice ? 0 : 0.15 * Math.max(0, parsedSalary - 5000);

    // Total annual cost combines salary and employer NI.
    const totalAnnualCost = parsedSalary + employerNi;

    // Hourly cost is the annual cost divided by the annual working hours.
    const hourlyCost = workingHoursPerYear > 0 ? totalAnnualCost / workingHoursPerYear : 0;

    return {
      employerNi,
      totalAnnualCost,
      hourlyCost,
      workingHoursPerYear,
    };
  }, [isApprentice, parsedHoursPerDay, parsedSalary]);

  const apiAuthHeader = useMemo(
    () => ({
      Authorization: `Basic ${btoa(`${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`)}`,
    }),
    []
  );

  const fetchLabourCosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/labour-costs`, {
        headers: apiAuthHeader,
      });
      setLabourCosts(response.data);
    } catch (err) {
      console.error('Failed to fetch labour costs', err);
      setError('Failed to fetch labour costs');
    }
    setLoading(false);
  }, [apiAuthHeader]);

  useEffect(() => {
    fetchLabourCosts();
  }, [fetchLabourCosts]);

  const handleShowModal = (cost?: LabourCost) => {
    if (cost) {
      setEditing(cost);
      setFormData({
        employeeEmail: cost.employeeEmail,
        cost: cost.cost.toString(),
        effectiveStartDate: cost.effectiveStartDate,
        effectiveEndDate: cost.effectiveEndDate || '',
      });
    } else {
      setEditing(null);
      setFormData({ employeeEmail: '', cost: '', effectiveStartDate: '', effectiveEndDate: '' });
    }
    setSalaryInput('');
    setHoursPerDayInput('8');
    setIsApprentice(false);
    setHelperTouched(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSalaryHelperChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHelperTouched(true);
    setSalaryInput(event.target.value);
  };

  const handleHoursPerDayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHelperTouched(true);
    setHoursPerDayInput(event.target.value);
  };

  const handleApprenticeToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHelperTouched(true);
    setIsApprentice(event.target.checked);
  };

  useEffect(() => {
    if (!helperTouched) {
      return;
    }

    const hourlyCost = helperCalculations.hourlyCost;
    if (!Number.isFinite(hourlyCost)) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      cost: hourlyCost > 0 ? hourlyCost.toFixed(2) : '0.00',
    }));
  }, [helperCalculations.hourlyCost, helperTouched]);

  const formattedEmployerNi = currencyFormatter.format(helperCalculations.employerNi);
  const formattedTotalAnnualCost = currencyFormatter.format(helperCalculations.totalAnnualCost);
  const formattedHourlyCost = currencyFormatter.format(helperCalculations.hourlyCost);
  const breakdownLine = helperTouched && helperCalculations.workingHoursPerYear
    ? `Hourly cost = (${currencyFormatter.format(parsedSalary)} + ${currencyFormatter.format(helperCalculations.employerNi)}) ÷ ${helperCalculations.workingHoursPerYear.toLocaleString('en-GB')} hours = ${formattedHourlyCost}`
    : 'Enter a salary and hours per day to calculate the hourly cost.';

  const handleSubmit = async () => {
    const payload = {
      employeeEmail: formData.employeeEmail,
      cost: parseFloat(formData.cost),
      effectiveStartDate: formData.effectiveStartDate,
      effectiveEndDate: formData.effectiveEndDate || null,
    };

    try {
      if (editing) {
        await axios.put(`${process.env.REACT_APP_API_URL}/labour-costs/${editing.id}`, payload, {
          headers: apiAuthHeader,
        });
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/labour-costs`, payload, {
          headers: apiAuthHeader,
        });
      }
      handleCloseModal();
      fetchLabourCosts();
    } catch (err) {
      console.error('Failed to save labour cost', err);
      setError('Failed to save labour cost');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this labour cost?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/labour-costs/${id}`, {
        headers: apiAuthHeader,
      });
      fetchLabourCosts();
    } catch (err) {
      console.error('Failed to delete labour cost', err);
      setError('Failed to delete labour cost');
    }
  };

  return (
    <div className="container mt-3">
      <h1>Labour Costs</h1>
      <Button className="mb-3" onClick={() => handleShowModal()}>Add Labour Cost</Button>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center"><Spinner animation="border" /></div>
      ) : (
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>ID</th>
              <th>Employee Email</th>
              <th>Cost</th>
              <th>Effective Start</th>
              <th>Effective End</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {labourCosts.map(cost => (
              <tr key={cost.id}>
                <td>{cost.id}</td>
                <td>{cost.employeeEmail}</td>
                <td>{cost.cost}</td>
                <td>{cost.effectiveStartDate}</td>
                <td>{cost.effectiveEndDate || '-'}</td>
                <td>
                  <Button variant="secondary" size="sm" className="me-2" onClick={() => handleShowModal(cost)}>Edit</Button>
                  <Button variant="danger" size="sm" onClick={() => handleDelete(cost.id)}>Delete</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Edit Labour Cost' : 'Add Labour Cost'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Employee Email</Form.Label>
              <Form.Control name="employeeEmail" value={formData.employeeEmail} onChange={handleChange} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="labourCostField">
              <Form.Label>Labour cost (hourly)</Form.Label>
              <Form.Control
                name="cost"
                type="number"
                step="0.01"
                value={formData.cost}
                onChange={handleChange}
                aria-describedby="labourCostHelp"
              />
              <Form.Text id="labourCostHelp" muted>
                This field is populated automatically by the salary helper below or can be set manually.
              </Form.Text>
            </Form.Group>
            <div className="p-3 mb-3 bg-light border rounded">
              <h6>Salary to hourly cost helper</h6>
              <Form.Group className="mb-3" controlId="helperSalary">
                <Form.Label>Annual gross salary (£)</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="decimal"
                  value={salaryInput}
                  onChange={handleSalaryHelperChange}
                  placeholder="e.g. 73,500"
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="helperHoursPerDay">
                <Form.Label>Hours per day</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.25"
                  value={hoursPerDayInput}
                  onChange={handleHoursPerDayChange}
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="helperApprentice">
                <Form.Check
                  type="checkbox"
                  label="Under 25 apprentice (no employer NI)"
                  checked={isApprentice}
                  onChange={handleApprenticeToggle}
                />
              </Form.Group>
              <div className="mb-2">
                <p className="mb-1"><strong>Employer NI:</strong> {formattedEmployerNi}</p>
                <p className="mb-1"><strong>Total annual cost:</strong> {formattedTotalAnnualCost}</p>
                <p className="mb-1"><strong>Hourly cost to company:</strong> {formattedHourlyCost}</p>
              </div>
              <small className="text-muted d-block">{breakdownLine}</small>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Effective Start Date</Form.Label>
              <Form.Control name="effectiveStartDate" type="date" value={formData.effectiveStartDate} onChange={handleChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Effective End Date</Form.Label>
              <Form.Control name="effectiveEndDate" type="date" value={formData.effectiveEndDate} onChange={handleChange} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit}>{editing ? 'Save Changes' : 'Create'}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LabourCostsPage;
